import React, { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';
import { onAuthStateChanged, signInAnonymously } from 'firebase/auth';
import { auth, db } from '../firebase';
import { getStatsCollectionRoot } from '../utils/teamStatsConfig';

const NAME_MAX = 80;
const TEXT_MAX = 280;

/**
 * Family comments for the Gladiator Gazette during a live game (Gladiators TV).
 */
export default function GameFanComments({ teamId, gameId, gameArchived }) {
  const [displayName, setDisplayName] = useState('');
  const [text, setText] = useState('');
  const [comments, setComments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [authReady, setAuthReady] = useState(false);

  const valid = typeof teamId === 'string' && teamId.trim() && typeof gameId === 'string' && gameId.trim();
  const canComment = valid && !gameArchived;

  const commentsCol = useMemo(() => {
    if (!valid) return null;
    return collection(db, getStatsCollectionRoot(), teamId, 'games', gameId, 'fanComments');
  }, [teamId, gameId, valid]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setAuthReady(true);
        return;
      }
      try {
        await signInAnonymously(auth);
      } catch (e) {
        console.error('Fan comments auth failed', e);
        setMessage('Could not connect to post a quote. Try refreshing.');
      } finally {
        setAuthReady(true);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!commentsCol || !canComment) {
      setComments([]);
      return undefined;
    }
    const q = query(commentsCol, orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setComments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => console.error('fanComments listen', err)
    );
    return () => unsub();
  }, [commentsCol, canComment]);

  if (!valid || gameArchived) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    const name = displayName.trim();
    const body = text.trim();
    if (!name || !body) {
      setMessage('Add your name and a short quote.');
      return;
    }
    if (!auth.currentUser) {
      setMessage('Still connecting… try again in a moment.');
      return;
    }
    setSubmitting(true);
    try {
      await addDoc(commentsCol, {
        displayName: name.slice(0, NAME_MAX),
        text: body.slice(0, TEXT_MAX),
        createdAt: serverTimestamp(),
        authorUid: auth.currentUser.uid,
      });
      setText('');
      setMessage('Quote sent! The coach may feature it in the Gladiator Gazette.');
    } catch (err) {
      console.error('fan comment create', err);
      setMessage('Could not send quote. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="game-fan-comments card card-glad mt-3 mb-3">
      <div className="card-body">
        <h3 className="h6 section-title mb-1">Send a quote to the Gazette</h3>
        <p className="small text-muted-brand mb-3">
          Cheer on the team — when the game ends, the coach can pick a family quote for The Gladiator Gazette.
        </p>
        <form onSubmit={handleSubmit} className="row g-2 align-items-end">
          <div className="col-12 col-md-3">
            <label className="small text-muted-brand mb-1" htmlFor="fan-quote-name">
              Your name
            </label>
            <input
              id="fan-quote-name"
              className="form-control form-control-sm bg-dark text-light border-0"
              value={displayName}
              onChange={(ev) => setDisplayName(ev.target.value.slice(0, NAME_MAX))}
              maxLength={NAME_MAX}
              placeholder="First name / family"
              disabled={submitting || !authReady}
              autoComplete="nickname"
            />
          </div>
          <div className="col-12 col-md-7">
            <label className="small text-muted-brand mb-1" htmlFor="fan-quote-text">
              Quote
            </label>
            <input
              id="fan-quote-text"
              className="form-control form-control-sm bg-dark text-light border-0"
              value={text}
              onChange={(ev) => setText(ev.target.value.slice(0, TEXT_MAX))}
              maxLength={TEXT_MAX}
              placeholder="What stood out tonight?"
              disabled={submitting || !authReady}
            />
          </div>
          <div className="col-12 col-md-2 d-grid">
            <button type="submit" className="btn btn-sm btn-teal" disabled={submitting || !authReady}>
              {submitting ? 'Sending…' : 'Send'}
            </button>
          </div>
        </form>
        {message && <p className="small mt-2 mb-0 text-muted-brand">{message}</p>}
        {comments.length > 0 && (
          <div className="mt-3">
            <div className="small text-muted-brand mb-1">Recent quotes ({comments.length})</div>
            <ul className="list-unstyled mb-0" style={{ maxHeight: 140, overflowY: 'auto' }}>
              {comments.slice(0, 8).map((c) => (
                <li key={c.id} className="small mb-1" style={{ color: '#e6e8ec' }}>
                  <strong>{c.displayName}</strong>
                  <span className="text-muted-brand"> — </span>
                  &ldquo;{c.text}&rdquo;
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
